import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import type { HistoryMonths } from '@/store/projectionsStore'
import { useViewportAnchoredMenu } from './useViewportAnchoredMenu'

const HISTORY_OPTIONS: Array<{ value: HistoryMonths; label: string }> = [
  { value: 3, label: '3 months' },
  { value: 6, label: '6 months' },
  { value: 12, label: '12 months' },
  { value: 24, label: '24 months' },
  { value: 'all', label: 'All months' },
]

const HISTORY_SHORT: Record<string, string> = {
  '3': '3 mo',
  '6': '6 mo',
  '12': '12 mo',
  '24': '24 mo',
  all: 'all',
}

const SECTION_LABEL =
  'px-3 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500'

type GridViewMenuProps = {
  historyMonths: HistoryMonths
  onHistoryMonthsChange: (months: HistoryMonths) => void
  onExpandAll: () => void
  onCollapseAll: () => void
}

export default function GridViewMenu({
  historyMonths,
  onHistoryMonthsChange,
  onExpandAll,
  onCollapseAll,
}: GridViewMenuProps) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const closeMenu = useCallback(() => setOpen(false), [])
  const { triggerRef, menuRef, menuStyle } = useViewportAnchoredMenu(open, closeMenu)

  useEffect(() => {
    if (!open) return
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (containerRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      setOpen(false)
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, menuRef])

  const menuPanel =
    open && menuStyle ? (
      <div
        ref={menuRef}
        role="menu"
        data-viewport-menu
        className="fixed z-[200] overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
        style={{
          top: menuStyle.top,
          left: menuStyle.left,
          minWidth: menuStyle.minWidth,
        }}
      >
        <p className={`${SECTION_LABEL} pt-2`}>History</p>
        {HISTORY_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            role="menuitemradio"
            aria-checked={historyMonths === opt.value}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
            onClick={() => {
              onHistoryMonthsChange(opt.value)
              setOpen(false)
            }}
          >
            <span
              className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                historyMonths === opt.value ? 'border-brand-600' : 'border-gray-300'
              }`}
              aria-hidden
            >
              {historyMonths === opt.value ? (
                <span className="h-2 w-2 rounded-full bg-brand-600" />
              ) : null}
            </span>
            {opt.label}
          </button>
        ))}
        <div className="my-1 border-t border-gray-100" role="separator" />
        <p className={`${SECTION_LABEL} pt-1`}>Sections</p>
        <button
          type="button"
          role="menuitem"
          className="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
          onClick={() => {
            onExpandAll()
            setOpen(false)
          }}
        >
          Expand all
        </button>
        <button
          type="button"
          role="menuitem"
          className="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
          onClick={() => {
            onCollapseAll()
            setOpen(false)
          }}
        >
          Collapse all
        </button>
      </div>
    ) : null

  return (
    <div className="relative" ref={containerRef} data-history-months>
      <div ref={triggerRef} className="inline-flex">
        <Button
          variant="outline"
          size="sm"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          View · {HISTORY_SHORT[String(historyMonths)]}
          <ChevronDownIcon
            className={`ml-1 h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </Button>
      </div>
      {menuPanel ? createPortal(menuPanel, document.body) : null}
    </div>
  )
}
